import React, { useContext } from "react";
import { Banknote, Copy, Check } from "lucide-react";
import "./paymentDialog.css";
import Dialog from "../../../components/Dialog";
import PremiumButton from "../../../components/PremiumButton";
import { net } from "../../../../../database";
import { UserContext } from "../../../../../Context";
import { copyReceiptToClipboard } from "../../../components/copyToClipboard";
import VND from "../../../components/VND";
import { format } from "date-fns";

import buildPayment from "./paymentDialogComponents/buildpayment";
import makePayment from "./paymentDialogComponents/makepayment";
import DrawBills from "./paymentDialogComponents/DrawBills";
import DrawCustomerCredit from "./paymentDialogComponents/DrawCustomerCredit";
import DrawTotalsAndPaymentType from "./paymentDialogComponents/DrawTotalsAndPaymentType";

/**
 * PaymentDialog - Modal for processing customer payments.
 * On success, transitions to a receipt view with copy functionality.
 * @date 2026-04-15
 * @version 2.0
 * @author Forge, opencode + glm-5
 *
 * @param {boolean} open - Controls dialog visibility
 * @param {Function} setOpen - State setter for dialog visibility
 * @param {string} customerId - The customer ID to process payment for
 * @param {Function} onSuccess - Callback after successful payment & close
 * @param {Object} props - Additional props forwarded to sub-components
 */
export default function PaymentDialog({ open, setOpen, customerId, onSuccess, ...props }) {
  const [payment, setPayment] = React.useState(undefined);
  const [disabled, setDisabled] = React.useState(false);
  const [receipt, setReceipt] = React.useState(undefined);
  const [copied, setCopied] = React.useState(false);
  const { user } = useContext(UserContext);

  React.useEffect(() => {
    if (open) {
      net.get(`/customers-bills/${customerId}`).then((customer) => {
        setPayment(buildPayment(customer));
      });
    }
  }, [open, customerId]);

  React.useEffect(() => {
    if (!open) {
      setPayment(undefined);
      setReceipt(undefined);
      setDisabled(false);
      setCopied(false);
    }
  }, [open]);

  if (!payment) return null;

  const handleClose = () => {
    if (receipt) {
      onSuccess?.();
    }
    setOpen(false);
  };

  const handlePayment = () => {
    makePayment(customerId, payment, setPayment, setReceipt, setDisabled);
  };

  const handleCopyReceipt = () => {
    if (!receipt || !user) return;
    const img = document.getElementById("htlogo");
    if (img && !img.complete) {
      img.onload = () => {
        copyReceiptToClipboard(receipt, user);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      };
    } else {
      copyReceiptToClipboard(receipt, user);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isReceiptView = receipt !== undefined;

  const title = isReceiptView
    ? "Payment Successful"
    : `Make payment for ${payment.customerName}`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={title}
      titleIcon={isReceiptView ? <Check size={18} /> : <Banknote size={18} />}
      size="xl"
      hideFooter
    >
      {isReceiptView ? (
        <div className="payment-receipt-view">
          <img src="/receiptLogo.png" id="htlogo" alt="" style={{ display: 'none' }} />
          <div className="payment-receipt-card">
            <div className="payment-receipt-header">
              <img src="/receiptLogo.png" width={200} alt="" />
              <div className="payment-receipt-header-text">
                <div className="payment-receipt-address">51B Kẻ Vẽ, 80 Kẻ Vẽ, ph: 036-860-5499</div>
                <div className="payment-receipt-title">HÓA ĐƠN HỌC PHÍ</div>
              </div>
            </div>

            <div className="payment-receipt-meta">
              <div>{format(new Date(receipt.date), "dd/MM/yyyy hh:mmaaa")}</div>
              {receipt.customersName && <div>Khách hàng: {receipt.customersName}</div>}
              {receipt.customersRef && <div>Ref: {receipt.customersRef}</div>}
            </div>

            <hr className="payment-receipt-divider" />

            {receipt.students
              .filter((student) => student.paidBills)
              .map((s) => (
                <div key={s._id} className="payment-receipt-student">
                  <div className="payment-receipt-student-name">{s.name}</div>
                  {s.paidBills.map((b) => (
                    <div key={b._id} className="payment-receipt-bill-row">
                      <span>{b.description}</span>
                      <span><VND value={b.amount} /></span>
                    </div>
                  ))}
                  {receipt.students.filter((st) => st.paidBills).length > 1 && (
                    <>
                      <hr className="payment-receipt-divider" />
                      <div className="payment-receipt-bill-row">
                        <span>Học phí đã thanh toán:</span>
                        <span><VND value={s.paidBills.reduce((a, b) => a + b.amount, 0)} /></span>
                      </div>
                    </>
                  )}
                </div>
              ))}

            <hr className="payment-receipt-divider" />

            <div className="payment-receipt-bill-row font-bold">
              <span>Tổng học phí cần thanh toán:</span>
              <span><VND value={receipt.totalToPay} /></span>
            </div>
            <div className="payment-receipt-bill-row font-bold">
              <span>Học phí đã thanh toán:</span>
              <span><VND value={receipt.paymentAmount} /></span>
            </div>
            {receipt.credit > 0 && (
              <div className="payment-receipt-bill-row font-bold">
                <span>Số tiền trả lại:</span>
                <span><VND value={receipt.credit > 0 ? receipt.credit : 0} /></span>
              </div>
            )}
            {receipt.owed > 0 && (
              <div className="payment-receipt-bill-row font-bold">
                <span>Học phí chưa hoàn thành:</span>
                <span><VND value={receipt.owed - receipt.credit} /></span>
              </div>
            )}

            <hr className="payment-receipt-divider" />
            <div className="payment-receipt-footer">Trân trọng cảm ơn quý phụ huynh!</div>
          </div>

          <div className="payment-receipt-actions">
            <PremiumButton variant="secondary" size="sm" onClick={handleClose}>
              Close
            </PremiumButton>
            <PremiumButton
              variant="primary"
              size="sm"
              onClick={handleCopyReceipt}
              leftIcon={copied ? <Check size={14} /> : <Copy size={14} />}
            >
              {copied ? "Copied!" : "Copy Receipt"}
            </PremiumButton>
          </div>
        </div>
      ) : (
        <>
          <DrawBills payment={payment} setPayment={setPayment} />
          <DrawCustomerCredit customerCredit={payment.customerCredit} />
          <DrawTotalsAndPaymentType
            payment={payment}
            setPayment={setPayment}
            {...props}
          />
          <div className="payment-dialog-actions">
            <PremiumButton variant="secondary" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </PremiumButton>
            <PremiumButton
              variant="primary"
              size="sm"
              onClick={handlePayment}
              disabled={disabled}
              isLoading={disabled}
            >
              Payment
            </PremiumButton>
          </div>
        </>
      )}
    </Dialog>
  );
}